import { Fragment, useState, useEffect } from "react";
import { arrayOf, bool, func } from "prop-types";
import {
  initialStateShape,
  sectionShape,
  facetGroupShape,
} from "./ReportBuilderTypes.js";
import ContainerFull from "../../../../Common/js/components/Container/ContainerFull.jsx";
import AddSections from "./components/AddSections/AddSections.jsx";
import Report from "./components/Report/Report.jsx";
import Button from "../Button/Button.jsx";

import {
  updateDraftReportPages,
  updateSavedReportPages,
  removePageFromSavedReport,
  removePageFromDraftReport,
  convertDraftReportToSavedReport,
  removePagesFromSavedReport,
  removePagesFromDraftReport,
  updateSavedReportTitle,
  deleteSavedReport,
  reorderSavedReportPages,
  reorderDraftReportPages,
} from "./ReportBuilderTypes.js";

const propTypes = {
  isAuthenticated: bool.isRequired,
  userReportState: initialStateShape.isRequired,
  sections: arrayOf(sectionShape).isRequired,
  facetGroups: arrayOf(facetGroupShape).isRequired,
  onDraftReportChange: func,
};

const ReportBuilder = (props) => {
  const {
    isAuthenticated: isAuthenticated,
    userReportState: initialState,
    sections: sections,
    facetGroups: facetGroups,
    onDraftReportChange,
  } = props;

  const [activeReport, setActiveReport] = useState({});

  const [state, setState] = useState(initialState);
  /*network requests*/

  useEffect(() => {
    const url = "api/sitecore/ReportBuilder/SaveReportBuilderData";
    const data = JSON.stringify(state);
    //update the server with the latest draft and saved information
    fetch(url, {
      method: "POST", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
      credentials: "same-origin", // include, *same-origin, omit
      headers: {
        "Content-Type": "application/json",
        // 'Content-Type': 'application/x-www-form-urlencoded',
      },
      redirect: "follow", // manual, *follow, error
      referrerPolicy: "no-referrer", // no-referrer, *client
      body: data, // body data type must match "Content-Type" header
    });
  }, [facetGroups, isAuthenticated, sections, state]);

  useEffect(() => {
    //sync the store with the current number of draft pages for the badge icon
    onDraftReportChange(state.draftReport);
  }, [onDraftReportChange, state.draftReport, state.draftReport.length]);

  /*event handlers*/

  const handleSavedReportDragEnd = (result, savedReportIndex) => {
    if (!result.destination) {
      return;
    }

    setState((prevState) => {
      return reorderSavedReportPages(
        savedReportIndex,
        result.source.index,
        result.destination.index,
        prevState,
      );
    });
  };

  const handleDraftReportDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    setState((prevState) => {
      return reorderDraftReportPages(
        result.source.index,
        result.destination.index,
        prevState,
      );
    });
  };

  const handleUpdateSavedReportTitle = (index, title) => {
    setState((prevState) => {
      return updateSavedReportTitle(index, title, prevState);
    });
  };

  const handleDeleteSavedReport = (index) => {
    setState((prevState) => {
      return deleteSavedReport(index, prevState);
    });
  };

  const handleRemoveAllSavedReportPages = (index, pages) => {
    setState((prevState) => {
      return removePagesFromSavedReport(index, pages, prevState);
    });
  };

  const handleRemoveAllDraftReportPages = (pages) => {
    setState((prevState) => {
      return removePagesFromDraftReport(pages, prevState);
    });
  };

  const handleCancelAddPages = () => {
    setActiveReport({});
  };

  const handleAddPages = (type, index) => {
    setActiveReport({ type: type, index: index });
  };

  const handleAddPagesToNewDraft = () => {
    setActiveReport({ type: "draftReport", index: undefined });
  };

  const handleRemoveDraftReportItem = (page) => {
    setState((prevState) => {
      return removePageFromDraftReport(page, prevState);
    });
  };

  const handleRemoveSavedReportItem = (index, page) => {
    setState((prevState) => {
      return removePageFromSavedReport(index, page, prevState);
    });
  };

  const handleSaveDraftReportStagedPages = (stagedPages) => {
    setActiveReport({});
    setState((prevState) => {
      return updateDraftReportPages(stagedPages, prevState);
    });
  };

  const handleSaveSavedReportStagedPages = (stagedPages) => {
    setActiveReport({});
    setState((prevState) => {
      return updateSavedReportPages(activeReport.index, stagedPages, prevState);
    });
  };

  const handleSaveDraftReport = (name) => {
    setState((prevState) => {
      return convertDraftReportToSavedReport(
        name,
        state.savedReports.length,
        state.draftReport,
        prevState,
      );
    });
  };

  const renderAddSections = () => {
    const pages =
      activeReport.type === "draftReport"
        ? state.draftReport
        : state.savedReports[activeReport.index].reportPages;

    const onSaveStagedPages =
      activeReport.type === "draftReport"
        ? handleSaveDraftReportStagedPages
        : handleSaveSavedReportStagedPages;

    return (
      <AddSections
        pages={pages}
        onCancelAddPages={handleCancelAddPages}
        onSaveStagedPages={onSaveStagedPages}
        sections={sections}
        facetGroups={facetGroups}
      />
    );
  };

  return (
    <section className="report-builder">
      <ContainerFull>
        {activeReport.type !== undefined ? (
          renderAddSections()
        ) : (
          <div className="report-builder__reports-container">
            <div className="report-builder__draft-report">
              {state.draftReport.length > 0 && (
                <Report
                  title="Draft Report"
                  type="draftReport"
                  isAuthenticated={isAuthenticated}
                  onAddPages={() => handleAddPages("draftReport")}
                  pages={state.draftReport}
                  onRemoveReportItem={(page) =>
                    handleRemoveDraftReportItem(page)
                  }
                  onRemoveAllReportItems={() => {
                    handleRemoveAllDraftReportPages(state.draftReport);
                  }}
                  onSaveDraftReport={handleSaveDraftReport}
                  onDragEnd={(result) => handleDraftReportDragEnd(result)}
                />
              )}
              {state.draftReport.length === 0 && (
                <div className="report-builder__start-new-report">
                  <h2 className="report-builder__start-new-report-title">
                    START A NEW REPORT
                  </h2>
                  <Button
                    onClick={handleAddPagesToNewDraft}
                    className="btn--primary-ghost"
                  >
                    Add Sections
                  </Button>
                </div>
              )}
            </div>
            {isAuthenticated && (
              <Fragment>
                <h2 className="report-builder__my-saved">My Saved Reports</h2>
                {state.savedReports.length > 0 && (
                  <div className="report-builder__saved-reports">
                    {state.savedReports.map((report, index) => (
                      <Report
                        key={report.title + index}
                        isAuthenticated={isAuthenticated}
                        title={report.reportTitle}
                        type="savedReport"
                        onAddPages={() => handleAddPages("savedReport", index)}
                        pages={report.reportPages}
                        onRemoveAllReportItems={() =>
                          handleRemoveAllSavedReportPages(
                            index,
                            report.reportPages,
                          )
                        }
                        onRemoveReportItem={(page) =>
                          handleRemoveSavedReportItem(index, page)
                        }
                        saveDraftReport={undefined}
                        onUpdateTitle={(title) =>
                          handleUpdateSavedReportTitle(index, title)
                        }
                        onDeleteSavedReport={() =>
                          handleDeleteSavedReport(index)
                        }
                        onDragEnd={(result) =>
                          handleSavedReportDragEnd(result, index)
                        }
                      />
                    ))}
                  </div>
                )}
                {state.savedReports.length === 0 && (
                  <div className="report-builder__no-saved-reports">
                    You have no saved reports
                  </div>
                )}
              </Fragment>
            )}
          </div>
        )}
      </ContainerFull>
    </section>
  );
};

ReportBuilder.propTypes = propTypes;
export default ReportBuilder;
